// Scroll Animation Utility - Performance Optimized with Routing Support
// Triggers animations when elements come into view with improved performance

let currentObserver = null;
let isObserverActive = false;

// Reset all animations and clean up observer
export const resetScrollAnimations = () => {
  // Clean up existing observer
  if (currentObserver) {
    currentObserver.disconnect();
    currentObserver = null;
  }
  
  // Reset all animated elements
  const allAnimatableElements = document.querySelectorAll(
    '.home-text-title, .gradient-divider, .home-text-paragraph, .service-card, .service-icon, .service-title, .service-subtitle, .service-features-item, .service-tag, .page-footer, .footer-brand, .footer-links-grid, .footer-newsletter, .footer-social-icon'
  );
  
  allAnimatableElements.forEach(element => {
    element.classList.remove('animate');
  });
  
  isObserverActive = false;
};

export const initScrollAnimations = () => {
  // Always reset first to handle route changes
  resetScrollAnimations();
  
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    // Still add animate classes for users who prefer reduced motion
    // but animations will be handled by CSS media query
    addAnimateClassesImmediately();
    return;
  }

  // Intersection Observer options - optimized for performance
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px 200px 0px', // Reduced from 300px for better timing
    threshold: [0, 0.1] // Multiple thresholds for better control
  };

  // Create observer with performance optimizations
  currentObserver = new IntersectionObserver((entries) => {
    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          // Add animate class to trigger animations
          entry.target.classList.add('animate');
          
          // For parent elements, also animate their children with staggered timing
          const childElements = entry.target.querySelectorAll(
            '.service-icon, .service-title, .service-subtitle, .service-features-item, .service-tag, .footer-brand, .footer-links-grid, .footer-newsletter, .footer-social-icon'
          );
          
          // Stagger child animations for better visual flow
          childElements.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('animate');
            }, index * 50); // 50ms stagger between elements
          });
          
          // Stop observing this element once animated (performance optimization)
          currentObserver?.unobserve(entry.target);
        }
      });
    });
  }, observerOptions);

  // Elements to observe - optimized selector list
  const elementsToObserve = [
    // HomeText elements
    '.home-text-title',
    '.gradient-divider', 
    '.home-text-paragraph',
    
    // ServiceInfos elements
    '.service-card',
    
    // PageFooter elements
    '.page-footer'
  ];

  // Start observing elements with error handling
  let observedCount = 0;
  elementsToObserve.forEach(selector => {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Only observe elements that aren't already animated
        if (!element.classList.contains('animate') && currentObserver) {
          currentObserver.observe(element);
          observedCount++;
        }
      });
    } catch (error) {
      console.warn(`Error observing elements with selector ${selector}:`, error);
    }
  });

  // Set observer as active
  isObserverActive = true;
  
  // Cleanup function for component unmounting
  return () => {
    resetScrollAnimations();
  };
};

// Fallback function for users with reduced motion preference
const addAnimateClassesImmediately = () => {
  const allAnimatableElements = document.querySelectorAll(
    '.home-text-title, .gradient-divider, .home-text-paragraph, .service-card, .service-icon, .service-title, .service-subtitle, .service-features-item, .service-tag, .page-footer, .footer-brand, .footer-links-grid, .footer-newsletter, .footer-social-icon'
  );
  
  allAnimatableElements.forEach(element => {
    element.classList.add('animate');
  });
};

// Optimized initialization with proper timing
const initAnimationsWhenReady = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Small delay to ensure all components are mounted
      setTimeout(initScrollAnimations, 100);
    });
  } else {
    // DOM is already loaded
    setTimeout(initScrollAnimations, 100);
  }
};

// Auto-initialize when script loads (for direct page loads)
if (typeof window !== 'undefined') {
  initAnimationsWhenReady();
} 